import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';

const TimePicker = ({ value, onChange, className = '', placeholder, minTime }) => {
  const { language } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(() => {
    if (value) {
      const [hour] = value.split(':');
      return parseInt(hour);
    }
    return 9;
  });
  const [selectedMinute, setSelectedMinute] = useState(() => {
    if (value) {
      const [, minute] = value.split(':');
      return parseInt(minute) || 0;
    }
    return 0;
  });
  const pickerRef = useRef(null);
  const isRTL = language === 'ar';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      const [hour, minute] = value.split(':');
      setSelectedHour(parseInt(hour));
      setSelectedMinute(parseInt(minute) || 0);
    } else if (minTime) {
      // If no value but minTime exists, set to minTime
      const [hour, minute] = minTime.split(':');
      setSelectedHour(parseInt(hour));
      setSelectedMinute(parseInt(minute) || 0);
    }
  }, [value, minTime]);

  const handleTimeSelect = (hour, minute) => {
    setSelectedHour(hour);
    setSelectedMinute(minute);
    const formattedHour = String(hour).padStart(2, '0');
    const formattedMinute = String(minute).padStart(2, '0');
    onChange(`${formattedHour}:${formattedMinute}`);
  };

  const formatDisplayTime = (timeString) => {
    if (!timeString) return '';
    const [hour, minute] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hour), parseInt(minute));
    return date.toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Filter hours based on minTime
  const getAvailableHours = () => {
    const allHours = Array.from({ length: 24 }, (_, i) => i);
    if (!minTime) return allHours;
    
    const [minHour, minMinute] = minTime.split(':').map(Number);
    return allHours.filter(hour => hour > minHour || (hour === minHour && minMinute === 0));
  };

  const getAvailableMinutes = (hour) => {
    const allMinutes = [0, 15, 30, 45];
    if (!minTime) return allMinutes;
    
    const [minHour, minMinute] = minTime.split(':').map(Number);
    if (hour > minHour) return allMinutes;
    if (hour === minHour) {
      // Only show minutes after minMinute
      return allMinutes.filter(min => min > minMinute);
    }
    return [];
  };

  const hours = getAvailableHours();
  const minutes = getAvailableMinutes(selectedHour);

  return (
    <div className="relative" ref={pickerRef} dir={isRTL ? 'rtl' : 'ltr'}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left bg-white flex items-center justify-between ${className}`}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-500'}>
          {value ? formatDisplayTime(value) : placeholder || 'Select time'}
        </span>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 z-50 min-w-[280px]">
          <div className="flex gap-4">
            {/* Hours */}
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-700 mb-2 text-center">Hours</h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {hours.map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => handleTimeSelect(hour, selectedMinute)}
                    className={`w-full px-3 py-2 rounded-lg text-sm transition ${
                      selectedHour === hour
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {String(hour).padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>

            {/* Minutes */}
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-700 mb-2 text-center">Minutes</h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {getAvailableMinutes(selectedHour).length > 0 ? (
                  getAvailableMinutes(selectedHour).map((minute) => (
                    <button
                      key={minute}
                      type="button"
                      onClick={() => handleTimeSelect(selectedHour, minute)}
                      className={`w-full px-3 py-2 rounded-lg text-sm transition ${
                        selectedMinute === minute
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {String(minute).padStart(2, '0')}
                    </button>
                  ))
                ) : (
                  <p className="text-xs text-gray-500 text-center py-4">No available minutes</p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">
                {formatDisplayTime(`${String(selectedHour).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;

